import React, { useEffect, useState } from 'react';
import { useRequests } from '../hooks/HRequests';
import { useHUser } from '../hooks/HUsers';
import GRequestsTable from '../../tables/GRequestsTable';
import { usePermissions } from '../hooks/HPermissions';

export default function RequestsTab() {
    const { loadAllRequests, requests, loading: requestsLoading, error: requestsError } = useRequests();
    const { users } = useHUser();
    const { createPermission, removePermission } = usePermissions();
    const [approvingRequest, setApprovingRequest] = useState(false);

    useEffect(() => { void loadAllRequests(); }, [loadAllRequests]);

    const handleApproveRequest = async (userId: string | number, objectId: string | number, row?: any) => {
        console.debug('[RequestsTabTabs] handleApproveRequest ENTER', { userId, objectId });
        const selectedUser = users.find((user) => String(user.id) === String(userId));
        let roleId = selectedUser?.role_id ?? selectedUser?.roleId;
        // If row provided, try to read role directly from it
        if ((roleId === undefined || roleId === null || String(roleId).trim() === '') && row) {
            roleId = row?.role_id ?? row?.roleId ?? row?.role;
            console.debug('[RequestsTabTabs] resolved roleId from passed row', { roleId });
        }
        // Fallback: try to read role from the matching request row if users are not yet loaded
        if (roleId === undefined || roleId === null || String(roleId).trim() === '') {
            const matchingRequest = requests?.find(r => {
                const u = String(r.user_id ?? r.userId ?? r.user ?? r.requester_id ?? r.requested_user_id ?? '');
                const o = String(r.object_id ?? r.objectId ?? r.object ?? r.item_id ?? r.resource_id ?? '');
                return u === String(userId) && o === String(objectId);
            });
            roleId = matchingRequest?.role_id ?? matchingRequest?.roleId ?? matchingRequest?.role;
            console.debug('[RequestsTabTabs] fallback roleId from requests list', { roleId, matchingRequestId: matchingRequest?.id ?? null });
        }
        // Default role_id to 3 if still missing
        if (roleId === undefined || roleId === null || String(roleId).trim() === '') {
            console.debug('[RequestsTabTabs] roleId missing - defaulting to 3');
            roleId = 3;
        }
        console.debug('[RequestsTabTabs] resolved selectedUser and roleId', { selectedUser: selectedUser?.id ?? null, roleId });

        if (!userId || !objectId || roleId === null || roleId === undefined || String(roleId).trim() === '') {
            console.debug('[RequestsTabTabs] handleApproveRequest EARLY RETURN', { userId, objectId, roleId });
            return;
        }

        setApprovingRequest(true);
        try {
            console.debug('[RequestsTabTabs] calling createPermission', { userId, objectId, roleId });
            const created = await createPermission(userId, objectId, roleId);
            console.debug('[RequestsTabTabs] createPermission returned', { created });
            if (created) {
                console.debug('[RequestsTabTabs] permission created, calling loadAllRequests');
                await loadAllRequests();
                console.debug('[RequestsTabTabs] loadAllRequests complete');
            }
        } catch (error) {
            console.error('[RequestsTabTabs] Failed to approve request:', error);
        } finally {
            setApprovingRequest(false);
            console.debug('[RequestsTabTabs] handleApproveRequest EXIT', { userId, objectId });
        }
    };

    const handleRevokeRequest = async (userId: string | number, objectId: string | number) => {
        if (!userId || !objectId) return;
        setApprovingRequest(true);
        try {
            const removed = await removePermission(userId, objectId);
            if (removed) await loadAllRequests();
        } catch (error) {
            console.error('Failed to revoke request:', error);
        } finally {
            setApprovingRequest(false);
        }
    };

    // expose for console debugging
    try {
        // @ts-ignore
        // attach to window so you can call from console: window.__handleApprove_tabs(userId, objectId)
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        (window as any).__handleApprove_tabs = handleApproveRequest;
    } catch (e) {}

    return (
        <div className='standard-div'>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className='standard-div-flex'>
                    <h1>Requests</h1>
                    {/* Refresh button removed — requests auto-load on mount */}
                </div>

                <div style={{ width: '100%' }}>
                    <GRequestsTable
                        requests={requests}
                        loading={requestsLoading}
                        error={requestsError}
                        onApprove={handleApproveRequest}
                        onRevoke={handleRevokeRequest}
                        approving={approvingRequest}
                        user={users.find((u) => u.id === undefined ? undefined : u)}
                    />
                </div>
            </div>
        </div>
    );
}

// (debug attachment happens inside component)
